import { useEffect, useState } from "react";

import { LogOut, Menu } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import AccountMenu from "./accountMenu.js";
import NotificationMenu from "./notifications.js";
import SidebarLogo from "../../assets/icons/ep-icon-blue.png";
import useClearLocalStorage from "../hooks/useClearLocalStorage";
import useServiceProviderPortalAccess from "../hooks/useServiceProviderPortalAccess";
import { Button } from "../common/customDesignedComponents/button";
import { userSigInAction } from "../../redux/slices/user/userSignInSlice";
import { userProfileAction } from "../../redux/slices/user/userProfileSlice";
import {
  reactPixelEventOnlyForCustomer,
  decryptPayload,
  isIosApp,
  redirectToLogin,
} from "../helpers/utils";
import { ROLES } from "../helpers/constants";
import useBasicPlanOnlyCustomer from "../hooks/useBasicPlanOnlyCustomer";

interface DashboardHeaderProps {
  userName?: string;
  onMenuClick?: () => void;
  isSidebarOpen?: boolean;
}

export const DashboardHeader = ({
  onMenuClick,
  isSidebarOpen = true,
}: DashboardHeaderProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { clearLocalStorageValues } = useClearLocalStorage();
  const [isBelow699, setIsBelow699] = useState(false);
  const [isIosNativeApp, setIsIosNativeApp] = useState(false);

  const { loggedInEmail } = useSelector((state: any) => state?.userProfile);

  const userRoles = decryptPayload(localStorage.getItem("roles")) || [];
  const showCustomerHeaderAction =
    userRoles.includes(ROLES.customer) ||
    userRoles.includes(ROLES.secondaryUser);
  const { isMonitoringTierOnly } = useBasicPlanOnlyCustomer();
  const { canUseServiceProviderPortal, goToServiceProviderPortal } =
    useServiceProviderPortalAccess();

  const handleSellingRefinancing = () => {
    navigate("/user-profile/selling-refinancing");
  };

  const handleSignOut = () => {
    dispatch(
      userProfileAction.logoutUser({ url: "logout", data: {}, token: true }),
    );
    dispatch(userSigInAction.successLogout());
    reactPixelEventOnlyForCustomer("Signout", { email: loggedInEmail });

    const iosFlag = isIosApp();
    clearLocalStorageValues("check_county");
    localStorage.setItem("ivokeNativeMethod", iosFlag.toString());

    redirectToLogin(navigate);
  };

  useEffect(() => {
    setIsIosNativeApp(isIosApp());
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 698px)");
    const update = () => setIsBelow699(!!mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  const showSellingRefinancing =
    showCustomerHeaderAction && !isMonitoringTierOnly;

  return (
    <header
      className={`h-16 bg-card border-b border-border flex items-center justify-between fixed top-0 right-0 z-40 transition-all duration-200 ${
        isBelow699
          ? "left-0"
          : isSidebarOpen
            ? "left-64"
            : "left-16"
      } ${isIosNativeApp ? "px-3" : "px-6"}`}
    >
      {/* Left - Menu Toggle + Logo (mobile) */}
      <div className="flex items-center gap-2 min-w-0 shrink-0">
        <button
          onClick={onMenuClick}
          className="py-2 px-1 text-muted-foreground hover:text-foreground transition-colors shrink-0"
          aria-label="Toggle menu"
        >
          <Menu className="h-5 w-5" />
        </button>

        {isBelow699 && !isSidebarOpen ? (
          <img
            src={SidebarLogo}
            alt="EquityProtect"
            className="h-7 w-auto shrink-0"
          />
        ) : null}
      </div>

      {/* Right - Notifications & actions */}
      <div className="flex items-center justify-end gap-2 min-w-0 flex-1 ml-2">
        <div className="shrink-0">
          <NotificationMenu />
        </div>

        {!isIosNativeApp ? (
          <AccountMenu hideLogout hideMobileBar />
        ) : null}

        {!isIosNativeApp && canUseServiceProviderPortal ? (
          <Button
            variant="outline"
            size="sm"
            onClick={goToServiceProviderPortal}
            className="rounded-[10px] shrink-0 px-3 sm:px-4 h-8 sm:h-9 text-xs sm:text-sm whitespace-nowrap"
          >
            <span className="max-[699px]:hidden">Service Provider portal</span>
            <span className="min-[700px]:hidden">SP portal</span>
          </Button>
        ) : null}

        {showSellingRefinancing ? (
          <Button
            size="sm"
            onClick={handleSellingRefinancing}
            className="rounded-[10px] bg-[#0395ff] hover:bg-[#007dd7] text-white shadow-sm shrink-0 px-3 sm:px-5 h-8 sm:h-9 text-xs sm:text-sm whitespace-nowrap"
          >
            {isIosNativeApp || isBelow699
              ? "Selling/Refi"
              : "I am Selling/Refinancing"}
          </Button>
        ) : null}

        {!isIosNativeApp ? (
          <Button
            variant="destructive"
            size="sm"
            onClick={handleSignOut}
            className="rounded-md px-6 max-[499px]:px-3 max-[499px]:h-8 max-[499px]:text-xs shrink-0"
          >
            <span className="max-[510px]:hidden">SIGN OUT</span>
            <LogOut className="h-4 w-4 min-[511px]:hidden" aria-label="Sign out" />
          </Button>
        ) : null}
      </div>
    </header>
  );
};
